import { useHandler } from "@redotech/react-util/hook";
import TrashIcon from "@redotech/redo-icons/trash-01.svg";
import type { RcoProductMetafieldsSegmentSchema } from "@redotech/redo-model/advanced-flow/advanced-flow-db-parser";
import type { MetafieldValueMatchKind } from "@redotech/redo-model/checkouts/product-metafields";
import { RedoButton } from "@redotech/redo-web/arbiter-components/buttons/redo-button";
import { RedoTextInput } from "@redotech/redo-web/arbiter-components/input/redo-text-input";
import { TextRedoListItem } from "@redotech/redo-web/arbiter-components/list/redo-list";
import { RedoSingleSelectDropdownInput } from "@redotech/redo-web/arbiter-components/select-dropdown/redo-single-select-dropdown-input";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import { ShopifyIdType } from "@redotech/shopify-util/id";
import { memo, useMemo, useState } from "react";

type Metafield = RcoProductMetafieldsSegmentSchema["metafields"][number];

export const ProductMetafieldsSelector = memo(
  function ProductMetafieldsSelector({
    metafields,
    onMetafieldsChanged,
  }: {
    metafields: Metafield[];
    onMetafieldsChanged: (metafields: Metafield[]) => void;
  }) {
    const [namespace, setNamespace] = useState("");
    const [key, setKey] = useState("");
    const [value, setValue] = useState("");
    const [valueMatchKind, setValueMatchKind] =
      useState<MetafieldValueMatchKind>("exact");
    const [resourceType, setResourceType] = useState<
      ShopifyIdType.Product | ShopifyIdType.ProductVariant
    >(ShopifyIdType.Product);

    const resourceTypeOptions: TextRedoListItem<
      ShopifyIdType.Product | ShopifyIdType.ProductVariant
    >[] = useMemo(
      () => [
        {
          type: "text",
          id: ShopifyIdType.Product,
          value: ShopifyIdType.Product,
          text: "Product metafield",
        },
        {
          type: "text",
          id: ShopifyIdType.ProductVariant,
          value: ShopifyIdType.ProductVariant,
          text: "Variant metafield",
        },
      ],
      [],
    );

    const selectedResourceTypeOption = useMemo(() => {
      return resourceTypeOptions.find(
        (option) => option.value === resourceType,
      );
    }, [resourceType, resourceTypeOptions]);

    const matchKindOptions: TextRedoListItem<MetafieldValueMatchKind>[] =
      useMemo(
        () => [
          { type: "text", id: "exact", value: "exact", text: "Exact match" },
          {
            type: "text",
            id: "partial",
            value: "partial",
            text: "Contains (partial match)",
          },
        ],
        [],
      );

    const selectedMatchKindOption = useMemo(() => {
      return matchKindOptions.find((option) => option.value === valueMatchKind);
    }, [valueMatchKind, matchKindOptions]);

    const handleAddMetafield = useHandler(() => {
      if (!namespace.trim() || !key.trim() || !value.trim()) {
        return;
      }

      const newMetafield: Metafield = {
        resourceType,
        namespace: namespace.trim(),
        key: key.trim(),
        value: value.trim(),
        valueMatchKind,
      };

      // Check if metafield already exists
      const exists = metafields.some(
        (m) =>
          m.resourceType === newMetafield.resourceType &&
          m.namespace === newMetafield.namespace &&
          m.key === newMetafield.key &&
          m.value === newMetafield.value &&
          m.valueMatchKind === newMetafield.valueMatchKind,
      );

      if (!exists) {
        onMetafieldsChanged([...metafields, newMetafield]);
      }

      // Clear form
      setNamespace("");
      setKey("");
      setValue("");
      setValueMatchKind("exact");
    });

    const handleRemoveMetafield = useHandler((index: number) => {
      const newMetafields = metafields.filter((_, i) => i !== index);
      onMetafieldsChanged(newMetafields);
    });

    return (
      <Flex dir="column" gap="md" w="full">
        <Flex dir="column" gap="sm" w="full">
          <RedoSingleSelectDropdownInput<
            ShopifyIdType.Product | ShopifyIdType.ProductVariant
          >
            label="Resource type"
            options={resourceTypeOptions}
            optionSelected={({ item }) => setResourceType(item.value)}
            selectedItem={selectedResourceTypeOption}
            size="xs"
          />
          <RedoTextInput
            label="Namespace"
            setValue={setNamespace}
            size="sm"
            value={namespace}
          />
          <RedoTextInput label="Key" setValue={setKey} size="sm" value={key} />
          <RedoTextInput
            label="Value"
            setValue={setValue}
            size="sm"
            value={value}
          />
          <RedoSingleSelectDropdownInput<MetafieldValueMatchKind>
            label="Match type"
            options={matchKindOptions}
            optionSelected={({ item }) => setValueMatchKind(item.value)}
            selectedItem={selectedMatchKindOption}
            size="xs"
          />
          <RedoButton
            disabled={!namespace.trim() || !key.trim() || !value.trim()}
            hierarchy="secondary"
            onClick={handleAddMetafield}
            size="xs"
            text="Add metafield"
          />
        </Flex>

        {metafields.length > 0 && (
          <Flex dir="column" gap="sm" w="full">
            <Text fontSize="xs" fontWeight="medium" textColor="secondary">
              Selected Metafields ({metafields.length})
            </Text>
            {metafields.map((metafield, index) => (
              <Flex
                align="center"
                borderBottomWidth="1px"
                borderColor="primary"
                borderStyle="solid"
                dir="row"
                gap="sm"
                justify="space-between"
                key={index}
                pb="xs"
                w="full"
              >
                <Flex dir="column" gap="xs">
                  <Text fontSize="xs" fontWeight="medium" textColor="primary">
                    {metafield.resourceType === "Product"
                      ? "Product"
                      : "Variant"}
                    : {metafield.namespace}.{metafield.key}
                  </Text>
                  <Text fontSize="xs" textColor="secondary">
                    Value: {metafield.value} (
                    {metafield.valueMatchKind === "partial"
                      ? "contains"
                      : "exact"}
                    )
                  </Text>
                </Flex>
                <RedoButton
                  hierarchy="tertiary"
                  IconLeading={TrashIcon}
                  onClick={() => handleRemoveMetafield(index)}
                  size="xs"
                />
              </Flex>
            ))}
          </Flex>
        )}
      </Flex>
    );
  },
);
